import {
  AlertColor,
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Paper,
  Switch,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';

import { ArrowBack } from '@mui/icons-material';
import arrow from '../../../assets/icons/Arrow.svg';
import calendar from '../../../assets/icons/calendar.svg';
import AlertToast from '../../../common/components/Alert';
import apiClient from '../../../api/apiClient';
import { API_ENDPOINTS } from '../../../api/endpoints';
import { useAuth } from '../../../authContext/useAuth';
import { useNavigate } from 'react-router-dom';
import { PermissionCategory, PermissionSubcategory } from '../../../utils/permissionConstants';

interface AllPermissionsResponse {
  data: Record<string, any>;
  message: string;
  status: number;
}

interface AllUsersResponse {
  data: UserInfo[];
  message: string;
  status: number;
}

interface UserInfo {
  id: string;
  fullName: string;
  username: string;
}

type PermissionMap = Record<string, Record<string, any>>;

export default function UserAccessControls() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);
  const [allPermissions, setAllPermissions] = useState<Record<string, any>>({});
  const [allUsers, setAllUsers] = useState<UserInfo[]>([]);

  const [selectedUserPermissions, setSelectedUserPermissions] = useState<Record<string, any>>({});
  const [permissionMap, setPermissionMap] = useState<PermissionMap>({});

  const [selectedUser, setselectedUser] = useState<{
    id: string;
    name: string;
  }>({ id: '', name: '' });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoadingUserPermissions, setIsLoadingUserPermissions] = useState(false);
  const [isUpdatingUserPermissions, setIsUpdatingUserPermissions] = useState(false);

  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    type: AlertColor;
  }>({
    open: false,
    message: '',
    type: 'error',
  });

  const [selectedPrimary, setSelectedPrimary] = useState<string | null>(null);
  const [selectedSecondary, setSelectedSecondary] = useState<string | null>(null);
  const [enabledPrimaryMenus, setEnabledPrimaryMenus] = useState<string[]>([]);
  const [changedPermissions, setChangedPermissions] = useState<{
    enable: Record<string, string[]>;
    disable: Record<string, string[]>;
  }>({ enable: {}, disable: {} });

  const isPermissionKeyEnabled = (menuKey: string, subMenuKey: string) => {
    const userMenu = selectedUserPermissions[menuKey];

    if (!userMenu) {
      return false;
    }

    if (Array.isArray(userMenu)) {
      return userMenu.includes(subMenuKey);
    }

    if (typeof userMenu === 'object' && userMenu !== null) {
      // Check if it's an empty object - treat as "not selected initially"
      if (Object.keys(userMenu).length === 0) {
        return false;
      }

      if (typeof subMenuKey === 'string' && subMenuKey.includes('_')) {
        const parts = subMenuKey.split('_');

        if (parts.length >= 2) {
          const secondaryKey = parts[0];
          const childKey = parts.slice(1).join('_');
          if (userMenu.hasOwnProperty(secondaryKey)) {
            const value = userMenu[secondaryKey];

            if (Array.isArray(value)) {
              return value.includes(childKey);
            }
            if (value && typeof value === 'object' && Object.keys(value).length === 0) {
              return false;
            }
          }
        }
      }

      if (userMenu.hasOwnProperty(subMenuKey)) {
        return true;
      }

      return false;
    }

    return false;
  };

  const hasAnyEnabledSubcategories = (categoryKey: string): boolean => {
    const categoryMap = permissionMap[categoryKey];
    if (!categoryMap) return false;
    return Object.values(categoryMap).some((enabled) => enabled === true);
  };

  const getSecondaryOptions = (primaryKey: string) => {
    const primaryData = allPermissions[primaryKey];
    if (!primaryData) return [];
    if (Array.isArray(primaryData)) {
      return primaryData;
    }
    if (typeof primaryData === 'object' && primaryData !== null) {
      return Object.keys(primaryData);
    }
    return [];
  };

  const getTertiaryOptions = (primaryKey: string, secondaryKey: string) => {
    const primaryData = allPermissions[primaryKey];
    if (!primaryData || typeof primaryData !== 'object') return [];
    const secondaryData = primaryData[secondaryKey];
    if (!secondaryData) return [];
    if (Array.isArray(secondaryData)) {
      return secondaryData;
    }
    return [];
  };

  const isSaveDisabled = () => {
    const enableKeys = Object.keys(changedPermissions.enable);
    const disableKeys = Object.keys(changedPermissions.disable);
    const isDisabled = enableKeys.length === 0 && disableKeys.length === 0;

    return isDisabled;
  };

  const computeChangedFromState = (
    mapState: PermissionMap,
    enabledPrimaries: string[],
  ): { enable: Record<string, string[]>; disable: Record<string, string[]> } => {
    const result: { enable: Record<string, string[]>; disable: Record<string, string[]> } = {
      enable: {},
      disable: {},
    };

    Object.keys(allPermissions).forEach((primaryKey) => {
      const original = selectedUserPermissions[primaryKey];
      const uiPrimaryEnabled = enabledPrimaries.includes(primaryKey);
      const originalPrimaryEnabled =
        !!original && (Array.isArray(original) || (typeof original === 'object' && original !== null));

      const secondaryOptions = getSecondaryOptions(primaryKey);

      if (uiPrimaryEnabled && !originalPrimaryEnabled) {
        const selectedSub = secondaryOptions.filter((sec) => mapState[primaryKey]?.[sec]);
        result.enable[primaryKey] = selectedSub.length > 0 ? selectedSub : [];
        selectedSub.forEach((sec) => {
          if (hasTertiaryLevel(primaryKey, sec)) {
            const tert = getTertiaryOptions(primaryKey, sec).filter(
              (t) => mapState[primaryKey]?.[`${sec}_${t}`],
            );
            if (tert.length > 0) {
              result.enable[sec] = tert;
            } else {
              result.enable[sec] = [];
            }
          }
        });
        return;
      }

      if (!uiPrimaryEnabled && originalPrimaryEnabled) {
        const selectedSub = secondaryOptions.filter((sec) => mapState[primaryKey]?.[sec]);
        result.disable[primaryKey] = selectedSub.length > 0 ? Array.from(new Set(selectedSub)) : [];
        selectedSub.forEach((sec) => {
          if (hasTertiaryLevel(primaryKey, sec)) {
            const tert = getTertiaryOptions(primaryKey, sec).filter(
              (t) => mapState[primaryKey]?.[`${sec}_${t}`],
            );
            if (tert.length > 0) {
              result.disable[sec] = tert;
            } else {
              result.disable[sec] = [];
            }
          }
        });
        return;
      }

      if (uiPrimaryEnabled && originalPrimaryEnabled) {
        const secEnable: string[] = [];
        const secDisable: string[] = [];

        secondaryOptions.forEach((sec) => {
          const uiSec = !!mapState[primaryKey]?.[sec];
          let origSec = false;
          if (Array.isArray(original)) {
            origSec = original.includes(sec);
          } else if (original && typeof original === 'object') {
            origSec = Object.prototype.hasOwnProperty.call(original, sec);
          }

          if (uiSec && !origSec) secEnable.push(sec);
          if (!uiSec && origSec) secDisable.push(sec);

          if (hasTertiaryLevel(primaryKey, sec)) {
            const tertOpts = getTertiaryOptions(primaryKey, sec);
            const tertEnable: string[] = [];
            const tertDisable: string[] = [];
            tertOpts.forEach((t) => {
              const uiT = !!mapState[primaryKey]?.[`${sec}_${t}`];
              let origT = false;
              if (Array.isArray(original)) {
                origT = original.includes(t);
              } else if (original && typeof original === 'object') {
                const origSecVal = original[sec];
                if (Array.isArray(origSecVal)) {
                  origT = origSecVal.length === 0 || origSecVal.includes(t);
                }
              }
              if (uiT && !origT) tertEnable.push(t);
              if (!uiT && origT) tertDisable.push(t);
            });
            if (tertEnable.length > 0 || (uiSec && !origSec && tertEnable.length === 0)) {
              result.enable[sec] = tertEnable;
            }
            if (tertDisable.length > 0 || (!uiSec && origSec && tertDisable.length === 0)) {
              result.disable[sec] = tertDisable;
            }
          }
        });

        if (secEnable.length > 0) result.enable[primaryKey] = secEnable;
        if (secDisable.length > 0) result.disable[primaryKey] = secDisable;
      }
    });

    // Cleanup: remove empty arrays for keys that have no tertiary level and are not needed
    Object.keys(result.enable).forEach((key) => {
      if (Object.keys(allPermissions).includes(key)) return;
      // key is likely a secondary; keep empty array only if it has tertiary
      const belongs = Object.keys(allPermissions).some((p) => hasTertiaryLevel(p, key));
      if (!belongs && Array.isArray(result.enable[key]) && result.enable[key].length === 0) {
        delete result.enable[key];
      }
    });
    Object.keys(result.disable).forEach((key) => {
      if (Object.keys(allPermissions).includes(key)) return;
      const belongs = Object.keys(allPermissions).some((p) => hasTertiaryLevel(p, key));
      if (!belongs && Array.isArray(result.disable[key]) && result.disable[key].length === 0) {
        delete result.disable[key];
      }
    });

    return result;
  };

  const hasTertiaryLevel = (primaryKey: string, secondaryKey: string) => {
    const primaryData = allPermissions[primaryKey];

    if (!primaryData || typeof primaryData !== 'object') {
      return false;
    }
    const secondaryData = primaryData[secondaryKey];
    const hasTertiary = Array.isArray(secondaryData) && secondaryData.length > 0;

    return hasTertiary;
  };

  const handleToggle = (toggledKey: string, parentKey?: string, grandParentKey?: string) => {
    let isCurrentlyEnabled = false;
    let willBeEnabled = false;
    let wasOriginallyEnabled = false;

    // case for tersary keys
    if (grandParentKey && parentKey) {
      isCurrentlyEnabled = permissionMap[grandParentKey]?.[`${parentKey}_${toggledKey}`] || false;
      willBeEnabled = !isCurrentlyEnabled;

      const originalGrandParentData = selectedUserPermissions[grandParentKey];
      if (originalGrandParentData === undefined || originalGrandParentData === null) {
        wasOriginallyEnabled = false;
      } else {
        const originalParentData = originalGrandParentData[parentKey];
        if (originalParentData === undefined || originalParentData === null) {
          wasOriginallyEnabled = false;
        } else if (Array.isArray(originalParentData)) {
          wasOriginallyEnabled = originalParentData.length === 0 || originalParentData.includes(toggledKey);
        } else {
          wasOriginallyEnabled = false;
        }
      }
    } else if (parentKey) {
      // case for secondary keys
      isCurrentlyEnabled = permissionMap[parentKey]?.[toggledKey] || false;
      willBeEnabled = !isCurrentlyEnabled;

      const originalParentData = selectedUserPermissions[parentKey];
      if (originalParentData === undefined || originalParentData === null) {
        wasOriginallyEnabled = false;
      } else if (Array.isArray(originalParentData)) {
        wasOriginallyEnabled = true;
      } else if (typeof originalParentData === 'object' && originalParentData !== null) {
        wasOriginallyEnabled =
          Object.keys(originalParentData).length === 0 ||
          Object.prototype.hasOwnProperty.call(originalParentData, toggledKey);
      } else {
        wasOriginallyEnabled = false;
      }

      if (willBeEnabled && wasOriginallyEnabled) {
        const tertiaryOptions = getTertiaryOptions(parentKey, toggledKey);
        const hasMismatchedTertiaryKeys = tertiaryOptions.some((tertiaryKey) => {
          const isCurrentlyTertiaryEnabled =
            permissionMap[parentKey]?.[`${toggledKey}_${tertiaryKey}`] || false;

          const originalGrandParentData = selectedUserPermissions[parentKey];
          let wasOriginallyTertiaryEnabled = false;

          if (Array.isArray(originalGrandParentData)) {
            wasOriginallyTertiaryEnabled = originalGrandParentData.includes(tertiaryKey);
          } else if (originalGrandParentData && typeof originalGrandParentData === 'object') {
            const originalSecondaryData = originalGrandParentData[toggledKey];
            if (Array.isArray(originalSecondaryData)) {
              wasOriginallyTertiaryEnabled = originalSecondaryData.includes(tertiaryKey);
            }
          }

          return !isCurrentlyTertiaryEnabled && wasOriginallyTertiaryEnabled;
        });

        if (hasMismatchedTertiaryKeys) {
          wasOriginallyEnabled = false;
        }
      }
    } else {
      // case for primary keys
      isCurrentlyEnabled = enabledPrimaryMenus.includes(toggledKey);
      willBeEnabled = !isCurrentlyEnabled;

      const originalData = selectedUserPermissions[toggledKey];
      if (originalData === undefined || originalData === null) {
        wasOriginallyEnabled = false;
      } else {
        wasOriginallyEnabled = Array.isArray(originalData)
          ? true
          : typeof originalData === 'object'
          ? true
          : false;
      }

      if (willBeEnabled && wasOriginallyEnabled) {
        const secondaries = getSecondaryOptions(toggledKey);
        const hasMismatchedSubKeys = secondaries.some((secondaryKey) => {
          const isCurrentlySubEnabled = permissionMap[toggledKey]?.[secondaryKey] || false;

          let wasOriginallySubEnabled = false;
          if (Array.isArray(originalData)) {
            wasOriginallySubEnabled = originalData.includes(secondaryKey);
          } else if (originalData && typeof originalData === 'object') {
            wasOriginallySubEnabled = originalData.hasOwnProperty(secondaryKey);
          }

          return !isCurrentlySubEnabled && wasOriginallySubEnabled;
        });

        if (hasMismatchedSubKeys) {
          wasOriginallyEnabled = false;
        }
      }
    }

    if (willBeEnabled === wasOriginallyEnabled) {
      let nextMap: PermissionMap = JSON.parse(JSON.stringify(permissionMap));
      let nextEnabled: string[] = [...enabledPrimaryMenus];

      if (grandParentKey && parentKey) {
        nextMap[grandParentKey] = {
          ...(nextMap[grandParentKey] || {}),
          [`${parentKey}_${toggledKey}`]: willBeEnabled,
        };
        setPermissionMap((prev) => ({
          ...prev,
          [grandParentKey]: {
            ...prev[grandParentKey],
            [`${parentKey}_${toggledKey}`]: willBeEnabled,
          },
        }));
      } else if (parentKey) {
        nextMap[parentKey] = { ...(nextMap[parentKey] || {}), [toggledKey]: willBeEnabled };
        setPermissionMap((prev) => ({
          ...prev,
          [parentKey]: {
            ...prev[parentKey],
            [toggledKey]: willBeEnabled,
          },
        }));
      } else {
        if (isCurrentlyEnabled) {
          nextEnabled = nextEnabled.filter((item) => item !== toggledKey);
          nextMap = { ...nextMap };
          if (nextMap[toggledKey]) {
            Object.keys(nextMap[toggledKey]).forEach((subKey) => {
              nextMap[toggledKey][subKey] = false;
            });
          }
          setEnabledPrimaryMenus((prev) => prev.filter((item) => item !== toggledKey));
          setPermissionMap((prev) => {
            const newMap = { ...prev };
            if (newMap[toggledKey]) {
              Object.keys(newMap[toggledKey]).forEach((subKey) => {
                newMap[toggledKey][subKey] = false;
              });
            }
            return newMap;
          });
        } else {
          nextEnabled = [...nextEnabled, toggledKey];
          setEnabledPrimaryMenus((prev) => [...prev, toggledKey]);
        }
      }

      const recomputed = computeChangedFromState(nextMap, nextEnabled);
      setChangedPermissions(recomputed);
      return;
    }

    let newChanged: {
      enable: Record<string, string[]>;
      disable: Record<string, string[]>;
    } = {
      enable: { ...changedPermissions.enable },
      disable: { ...changedPermissions.disable },
    };

    if (willBeEnabled) {
      if (grandParentKey && parentKey) {
        const current = newChanged.enable[parentKey] || [];
        newChanged.enable[parentKey] = Array.from(new Set([...current, toggledKey]));

        const grandParentCurrent = newChanged.enable[grandParentKey] || [];
        newChanged.enable[grandParentKey] = Array.from(new Set([...grandParentCurrent, parentKey]));
        if (newChanged.disable[parentKey]) {
          newChanged.disable[parentKey] = newChanged.disable[parentKey].filter(
            (k: string) => k !== toggledKey,
          );
          if (newChanged.disable[parentKey].length === 0) delete newChanged.disable[parentKey];
        }
        if (newChanged.disable[grandParentKey]) {
          newChanged.disable[grandParentKey] = newChanged.disable[grandParentKey].filter(
            (k: string) => k !== parentKey,
          );
          if (newChanged.disable[grandParentKey].length === 0) delete newChanged.disable[grandParentKey];
        }
      } else if (parentKey) {
        const current = newChanged.enable[parentKey] || [];
        newChanged.enable[parentKey] = Array.from(new Set([...current, toggledKey]));

        const allSecondaryOptions = getSecondaryOptions(parentKey);
        const manuallySelectedSecondaries = allSecondaryOptions.filter((secondaryKey) => {
          if (secondaryKey === toggledKey) {
            return willBeEnabled;
          }
          return permissionMap[parentKey]?.[secondaryKey] || false;
        });

        newChanged.enable[parentKey] = manuallySelectedSecondaries;

        Object.keys(newChanged.enable).forEach((key) => {
          if (
            allSecondaryOptions.includes(key) &&
            key !== parentKey &&
            !manuallySelectedSecondaries.includes(key)
          ) {
            delete newChanged.enable[key];
          }
        });

        const hasTertiary = hasTertiaryLevel(parentKey, toggledKey);
        if (hasTertiary) {
          newChanged.enable[toggledKey] = [];
        } else {
          delete newChanged.enable[toggledKey];
        }

        if (newChanged.disable[parentKey]) {
          newChanged.disable[parentKey] = newChanged.disable[parentKey].filter(
            (k: string) => k !== toggledKey,
          );
          if (newChanged.disable[parentKey].length === 0) delete newChanged.disable[parentKey];
        }
        if (newChanged.disable[toggledKey]) {
          delete newChanged.disable[toggledKey];
        }
      } else {
        const secondaries = getSecondaryOptions(toggledKey);

        const selectedSubcategories = secondaries.filter((secondaryKey) => {
          return permissionMap[toggledKey]?.[secondaryKey] || false;
        });

        if (selectedSubcategories.length === 0) {
          newChanged.enable[toggledKey] = [];
        } else {
          newChanged.enable[toggledKey] = selectedSubcategories;
        }

        if (newChanged.disable[toggledKey]) {
          delete newChanged.disable[toggledKey];
        }
      }
    } else {
      if (grandParentKey && parentKey) {
        const current = newChanged.disable[parentKey] || [];
        newChanged.disable[parentKey] = Array.from(new Set([...current, toggledKey]));

        if (newChanged.enable[parentKey]) {
          newChanged.enable[parentKey] = newChanged.enable[parentKey].filter((k: string) => k !== toggledKey);
          if (newChanged.enable[parentKey].length === 0) delete newChanged.enable[parentKey];
        }
      } else if (parentKey) {
        const current = newChanged.disable[parentKey] || [];
        newChanged.disable[parentKey] = Array.from(new Set([...current, toggledKey]));

        const hasTertiary = hasTertiaryLevel(parentKey, toggledKey);
        if (hasTertiary) {
          newChanged.disable[toggledKey] = [];
        } else {
          delete newChanged.disable[toggledKey];
        }

        {
          const allSecondaryOptions = getSecondaryOptions(parentKey);
          const previousEnabled = newChanged.enable[parentKey] || [];
          const filtered = previousEnabled.filter((k: string) => k !== toggledKey);

          const stillEnabledFromUI = allSecondaryOptions.filter((sec) => {
            if (sec === toggledKey) return false;
            return permissionMap[parentKey]?.[sec] || false;
          });

          const finalEnabled = Array.from(new Set<string>([...filtered, ...stillEnabledFromUI]));

          if (finalEnabled.length > 0) {
            newChanged.enable[parentKey] = finalEnabled;
          } else {
            delete newChanged.enable[parentKey];
          }
        }
        if (newChanged.enable[toggledKey]) {
          delete newChanged.enable[toggledKey];
        }
      } else {
        const secondaries = getSecondaryOptions(toggledKey);

        const selectedSubcategories = secondaries.filter((secondaryKey) => {
          return permissionMap[toggledKey]?.[secondaryKey] || false;
        });

        if (selectedSubcategories.length === 0) {
          newChanged.disable[toggledKey] = [];
        } else {
          const prev = newChanged.disable[toggledKey] || [];
          const union = Array.from(new Set<string>([...prev, ...selectedSubcategories]));
          newChanged.disable[toggledKey] = union;
        }

        secondaries.forEach((secondaryKey) => {
          const hasTertiary = hasTertiaryLevel(toggledKey, secondaryKey);
          if (hasTertiary) {
            newChanged.disable[secondaryKey] = [];
          } else {
            if (newChanged.disable[secondaryKey]?.length === 0) {
              delete newChanged.disable[secondaryKey];
            }
          }
        });

        if (newChanged.enable[toggledKey]) {
          delete newChanged.enable[toggledKey];
        }
      }
    }

    setChangedPermissions(newChanged);

    if (grandParentKey && parentKey) {
      setPermissionMap((prev) => ({
        ...prev,
        [grandParentKey]: {
          ...prev[grandParentKey],
          [`${parentKey}_${toggledKey}`]: willBeEnabled,
        },
      }));
    } else if (parentKey) {
      if (!willBeEnabled) {
        setPermissionMap((prev) => {
          const newMap = { ...prev };
          if (newMap[parentKey]) {
            newMap[parentKey][toggledKey] = false;
            const tertiaryOptions = getTertiaryOptions(parentKey, toggledKey);
            tertiaryOptions.forEach((tertiaryKey) => {
              newMap[parentKey][`${toggledKey}_${tertiaryKey}`] = false;
            });
          }
          return newMap;
        });
        setSelectedSecondary(null);
      } else {
        setPermissionMap((prev) => ({
          ...prev,
          [parentKey]: {
            ...prev[parentKey],
            [toggledKey]: true,
          },
        }));

        // Restore original tertiary states when re-enabling a secondary category
        const originalParentData = selectedUserPermissions[parentKey];
        if (originalParentData && typeof originalParentData === 'object') {
          const originalSecondaryData = originalParentData[toggledKey];
          if (Array.isArray(originalSecondaryData)) {
            // Restore tertiary categories
            setPermissionMap((prev) => {
              const newMap = { ...prev };
              originalSecondaryData.forEach((tertiaryKey) => {
                newMap[parentKey][`${toggledKey}_${tertiaryKey}`] = true;
              });
              return newMap;
            });
          }
        }
      }
    } else {
      if (!willBeEnabled) {
        setEnabledPrimaryMenus((prev) => prev.filter((item) => item !== toggledKey));
        setPermissionMap((prev) => {
          const newMap = { ...prev };
          if (newMap[toggledKey]) {
            Object.keys(newMap[toggledKey]).forEach((subKey) => {
              newMap[toggledKey][subKey] = false;
            });
          }
          return newMap;
        });
        setSelectedPrimary(null);
        setSelectedSecondary(null);
      } else {
        setEnabledPrimaryMenus((prev) => [...prev, toggledKey]);

        // Restore original subcategory states when re-enabling a primary category
        const originalData = selectedUserPermissions[toggledKey];
        if (originalData && typeof originalData === 'object' && !Array.isArray(originalData)) {
          setPermissionMap((prev) => {
            const newMap = { ...prev };
            if (!newMap[toggledKey]) {
              newMap[toggledKey] = {};
            }

            // Restore secondary categories
            Object.keys(originalData).forEach((secondaryKey) => {
              const secondaryData = originalData[secondaryKey];
              if (Array.isArray(secondaryData)) {
                newMap[toggledKey][secondaryKey] = true;

                secondaryData.forEach((tertiaryKey) => {
                  newMap[toggledKey][`${secondaryKey}_${tertiaryKey}`] = true;
                });
              } else if (secondaryData !== null && secondaryData !== undefined) {
                newMap[toggledKey][secondaryKey] = true;
              }
            });

            return newMap;
          });
        } else if (Array.isArray(originalData)) {
          setPermissionMap((prev) => {
            const newMap = { ...prev };
            if (!newMap[toggledKey]) {
              newMap[toggledKey] = {};
            }

            // Restore all secondary categories
            originalData.forEach((secondaryKey) => {
              newMap[toggledKey][secondaryKey] = true;
            });

            return newMap;
          });
        }
      }
    }
  };

  const handleSelectUserButtonPress = (event: React.MouseEvent<HTMLButtonElement>, value?: string) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSelectUserMenuClose = async (userId?: string, userName?: string) => {
    if (userId === undefined) {
      setAnchorEl(null);
      return;
    }

    setselectedUser({
      id: userId || '',
      name: userName || '',
    });
    setAnchorEl(null);
    setSelectedPrimary(null);
    setSelectedSecondary(null);
  };

  const snakeToTitleCase = (input: string): string => {
    return input
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const fetchSelectedUserPermission = useCallback(async () => {
    if (selectedUser.id === '') {
      return;
    }

    setIsLoadingUserPermissions(true);
    setEnabledPrimaryMenus([]);
    try {
      const userPermissionsResponseObject = await apiClient({
        url: API_ENDPOINTS.users.ADMIN_USER_GET_PERMISSION_BY_ID,
        method: 'POST',
        data: {
          userId: selectedUser.id,
        },
        responseType: 'json',
      });

      const userPermissionsResponse = userPermissionsResponseObject.data;
      const permissionsData = userPermissionsResponse.data as Record<string, any>;
      setSelectedUserPermissions(permissionsData);
    } catch (error) {
      console.error('Error fetching Selected User Permissions', error);
    }

    setIsLoadingUserPermissions(false);
  }, [selectedUser.id]);

  useEffect(() => {
    fetchSelectedUserPermission();
  }, [fetchSelectedUserPermission]);

  useEffect(() => {
    if (Object.keys(allPermissions).length === 0) {
      return;
    }

    const initMap: PermissionMap = {};
    let enabledMainMenus: string[] = [];

    Object.entries(allPermissions).forEach(([menuKey, menuData]) => {
      const userMenu = selectedUserPermissions[menuKey];

      const hasUserMenu =
        userMenu && (Array.isArray(userMenu) ? true : typeof userMenu === 'object' ? true : false);

      if (hasUserMenu) {
        enabledMainMenus.push(menuKey);
      }

      initMap[menuKey] = {};

      if (Array.isArray(menuData)) {
        menuData.forEach((sub) => {
          const userSubMenus = new Set(Array.isArray(userMenu) ? userMenu : []);
          const isEnabled = userSubMenus.has(sub);
          initMap[menuKey][sub] = isEnabled;
        });
      } else if (typeof menuData === 'object' && menuData !== null) {
        Object.entries(menuData).forEach(([subKey, subData]) => {
          if (Array.isArray(subData)) {
            const secondaryEnabled =
              userMenu && typeof userMenu === 'object' && userMenu !== null
                ? Object.prototype.hasOwnProperty.call(userMenu, subKey)
                : false;
            initMap[menuKey][subKey] = secondaryEnabled;

            subData.forEach((item) => {
              const key = `${subKey}_${item}`;
              const itemEnabled = Array.isArray(userMenu?.[subKey])
                ? userMenu[subKey].length === 0 || userMenu[subKey].includes(item)
                : false;

              initMap[menuKey][key] = itemEnabled;
            });
          } else if (subData === null) {
            const subcategoryEnabled = Array.isArray(userMenu)
              ? userMenu.includes(subKey)
              : typeof userMenu === 'object' && userMenu !== null
              ? Object.keys(userMenu).length === 0
              : false;

            initMap[menuKey][subKey] = subcategoryEnabled;
          }
        });
      }
    });

    setEnabledPrimaryMenus(enabledMainMenus);
    setPermissionMap(initMap);
  }, [allPermissions, selectedUserPermissions]);

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoadingPermissions(true);
      try {
        const allPermissionsResponseObject = await apiClient({
          url: API_ENDPOINTS.users.ADMIN_USER_CONTROLS_PERMISSIONS_GET,
          method: 'GET',
          responseType: 'json',
        });
        const allPermissionsResponse: AllPermissionsResponse = allPermissionsResponseObject.data;
        setAllPermissions(allPermissionsResponse.data);
        if (user?.crn && user?.id) {
          const fetchUserResponse = await apiClient({
            url: API_ENDPOINTS.users.CUSTOMER_LIST_CRN,
            method: 'POST',
            data: {
              userCrn: user.crn,
              userId: user.id,
            },
            responseType: 'json',
          });
          const usersDataInfo: AllUsersResponse = fetchUserResponse.data;
          setAllUsers(usersDataInfo.data);
        } else {
          console.error('User not found or invalid user data');
        }
        setIsLoadingPermissions(false);
      } catch (error) {
        setIsLoadingPermissions(false);
      }
    };
    fetchInitialData();
  }, []);

  const isSaveDisabledValue = isSaveDisabled();

  const handleSaveChanges = async () => {
    setIsUpdatingUserPermissions(true);
    const payload = {
      userId: selectedUser.id,
      menuEnable: changedPermissions.enable,
      menuDisable: changedPermissions.disable,
    };

    try {
      const updatePermissionsResponseObject = await apiClient({
        url: API_ENDPOINTS.users.ADMIN_USER_UPDATE_PERMISSION_BY_ID,
        method: 'POST',
        data: payload,
        responseType: 'json',
      });
      setToast({
        open: true,
        message: 'User permissions updated successfully!',
        type: 'success',
      });
      setChangedPermissions({ enable: {}, disable: {} });
      // Refresh the selected user's permissions from server to sync UI baseline
      try {
        await fetchSelectedUserPermission();
      } catch (refreshError) {
        console.error('Error refreshing user permissions after save', refreshError);
      }
    } catch (error) {
      console.error('Save failed:', error);
      setToast({
        open: true,
        message: 'User permissions update failed!',
        type: 'error',
      });
    }
    setIsUpdatingUserPermissions(false);
  };
  return (
    <Paper elevation={10} className="flex flex-col gap-4 h-full overflow-hidden">
      <AlertToast
        open={toast.open}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        type={toast.type}
      />

      <div className="flex justify-between gap-4 flex-col align-center h-full overflow-y-scroll">
        <button className="text-blue-600 font-medium flex items-center" onClick={() => navigate(-1)}>
          <ArrowBack className="mr-1" /> Back
        </button>
        <Typography variant="h6">User Access Controls</Typography>
        <p className="text-[#767676]">
          Manage user roles and permissions to ensure secure access to the system.
        </p>

        {isLoadingPermissions && (
          <div className="h-full">
            <div className="flex items-center justify-center border rounded-lg p-4 w-full h-full space-x-4">
              <CircularProgress color="primary" size={25} />
              <Typography>Please wait...</Typography>
            </div>
          </div>
        )}

        {!isLoadingPermissions && (
          <div className="relative">
            <Button
              onClick={handleSelectUserButtonPress}
              disabled={isUpdatingUserPermissions}
              variant="outlined"
              sx={{
                height: { xs: '28px', sm: '30px', md: '48px' },
                px: { xs: 1, sm: 2 },
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                gap: { xs: 0.5, sm: 1 },
                borderColor: '#5547dd',
                backgroundColor: 'white',
                color: '#5547dd',
                minWidth: 0,
                maxWidth: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <img src={calendar} alt="Calendar" width={18} height={18} />
                <Box sx={{ display: 'block', maxWidth: '100%' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {selectedUser.name === '' ? 'Select User' : selectedUser.name}
                  </Typography>
                </Box>
                <img src={arrow} alt="Arrow" width={18} height={18} />
              </Box>
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => handleSelectUserMenuClose()}
              PaperProps={{
                sx: {
                  borderRadius: '8px',
                  top: '10px',
                  boxShadow: '0 6px 10px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              {allUsers.map((user) => (
                <MenuItem
                  key={user.id}
                  onClick={() => handleSelectUserMenuClose(user.id, user.fullName)}
                  sx={(theme) => ({
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  })}
                >
                  <Typography variant="button" textTransform={'none'}>
                    {user.fullName}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </div>
        )}

        {selectedUser.id === '' && !isLoadingPermissions && (
          <div className="h-full flex items-center justify-center border rounded-lg p-4 flex-1">
            <Typography>Please select a user to set controls</Typography>
          </div>
        )}

        {selectedUser.id !== '' && !isLoadingPermissions && isLoadingUserPermissions && (
          <div className="h-full flex items-center justify-center border rounded-lg p-4 flex-1 space-x-5">
            <CircularProgress color="primary" size={25} />
            <Typography>Please wait...</Typography>
          </div>
        )}

        {selectedUser.id !== '' && !isLoadingUserPermissions && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1 overflow-hidden">
            <div className="overflow-y-auto max-h-[70vh] p-4 rounded-xl border">
              <Typography variant="body1" className="mb-4">
                Primary Controls
              </Typography>
              {Object.keys(permissionMap).map((permission) => (
                <div
                  key={permission}
                  className="rounded-lg overflow-hidden cursor-pointer border p-2 my-2"
                  onClick={() => {
                    if (enabledPrimaryMenus.includes(permission)) {
                      setSelectedPrimary(permission);
                      setSelectedSecondary(null);
                    }
                  }}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      <Typography variant="button" fontSize={16} fontWeight={600}>
                        {snakeToTitleCase(permission)}
                      </Typography>
                    </div>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <Switch
                        checked={enabledPrimaryMenus.includes(permission)}
                        onChange={() => handleToggle(permission)}
                        disabled={isUpdatingUserPermissions}
                        color="primary"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="overflow-y-auto max-h-[70vh] p-4 rounded-xl border">
              <Typography variant="body1" className="mb-4">
                Secondary Controls
              </Typography>
              {selectedPrimary ? (
                getSecondaryOptions(selectedPrimary).length === 0 ? (
                  <Typography className="text-gray-500">
                    No secondary controls for <strong>{snakeToTitleCase(selectedPrimary)}</strong>
                  </Typography>
                ) : (
                  getSecondaryOptions(selectedPrimary).map((secondaryKey) => (
                    <div
                      key={secondaryKey}
                      className="rounded-lg overflow-hidden cursor-pointer border p-2 my-2"
                      onClick={() => {
                        if (permissionMap[selectedPrimary]?.[secondaryKey]) {
                          setSelectedSecondary(secondaryKey);
                        }
                      }}
                    >
                      <div className="flex justify-between items-center">
                        <Typography fontWeight={600}>{snakeToTitleCase(secondaryKey)}</Typography>
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <Switch
                            checked={permissionMap[selectedPrimary]?.[secondaryKey] || false}
                            onChange={() => handleToggle(secondaryKey, selectedPrimary)}
                            color="primary"
                            disabled={isUpdatingUserPermissions}
                          />
                        </div>
                      </div>
                    </div>
                  ))
                )
              ) : (
                <Typography className="text-gray-500">
                  Select a primary control to view secondary controls.
                </Typography>
              )}
            </div>

            <div className="overflow-y-auto max-h-[70vh] p-4 rounded-xl border">
              <Typography variant="body1" className="mb-4">
                Tertiary Controls
              </Typography>
              {selectedPrimary && selectedSecondary ? (
                hasTertiaryLevel(selectedPrimary, selectedSecondary) ? (
                  getTertiaryOptions(selectedPrimary, selectedSecondary).map((tertiaryKey) => (
                    <div key={tertiaryKey} className="cursor-pointer border rounded-lg p-2 my-2">
                      <div className="flex justify-between items-center">
                        <Typography fontWeight={600}>{snakeToTitleCase(tertiaryKey)}</Typography>
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <Switch
                            checked={
                              permissionMap[selectedPrimary]?.[`${selectedSecondary}_${tertiaryKey}`] || false
                            }
                            onChange={() => handleToggle(tertiaryKey, selectedSecondary, selectedPrimary)}
                            color="primary"
                            disabled={isUpdatingUserPermissions}
                          />
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <Typography className="text-gray-500">
                    No tertiary controls for <strong>{snakeToTitleCase(selectedSecondary)}</strong>
                  </Typography>
                )
              ) : (
                <Typography className="text-gray-500">
                  Select a secondary control to view tertiary controls.
                </Typography>
              )}
            </div>
          </div>
        )}

        {selectedUser.id !== '' && !isLoadingUserPermissions && (
          <div className="flex mb-5">
            <div className="flex-1"></div>
            <Button
              variant="contained"
              disabled={isUpdatingUserPermissions || isSaveDisabledValue}
              className="h-12 px-5 py-3 rounded-lg bg-[#5547dd] text-white hover:bg-[#4436cc] w-full md:w-auto"
              onClick={handleSaveChanges}
              sx={{
                fontWeight: 700,
                fontSize: '14px',
                lineHeight: '21px',
                textTransform: 'none',
              }}
            >
              {isUpdatingUserPermissions ? 'Saving Changes...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>
    </Paper>
  );
}
